import { Component, ojs, app, state, v } from "modular-openscriptjs";
import { dom, tool } from "../../../fotastart";
import {
    pageInfo,
    scrollToTop,
    setCurrentLocationCoordinates,
    either,
    oneOf,
    formatThousand,
    formatRating,
    randomInt,
    empty,
    inform,
    searchPlaceholder,
} from "../../../helpers";
import { PAGES, api, routes, STRINGS, dc } from "../../../declarations";
import { $e } from "../../../ojs.events";

const h = app("h");
const broker = app("broker");

export default class BusinessesPage extends Component {
    page = 1;
    total = 0;
    lastPage = false;
    isLoading = false;
    filters = state({ nearby: false, all: true, deals: false });

    // Bumped on every dispatched request (search, filter change, geo
    // update, pagination). A response is only allowed to touch the DOM
    // if its own sequence number still matches the latest one - the
    // searchQuery handler below deliberately resets isLoading to let a
    // fresh search jump the queue ahead of a slow in-flight request, so
    // this is what stops that now-stale request's response from landing
    // after the newer one and overwriting it.
    searchSeq = 0;

    resetMap() {
        this.page = 1;
        this.total = 0;
        this.lastPage = false;
        this.isLoading = false;

        // Also invalidate any request still in flight at the moment of the
        // reset, even before a replacement request goes out.
        this.searchSeq++;
    }

    // this ensure pagination is stable
    $$ojs = {
        routeChanged: () => this.resetMap(),
    };

    $$app = {
        has: {
            searchQuery: (ed, event) => {
                this.page = 1;
                this.total = 0;
                this.lastPage = false;
                this.isLoading = false;
                this.searchSeq++;
                dom.empty(dom.id(`b-profile`));
                this.getBusinesses("b-profile");
            },
            searchInputClick: (ed, event) => {
                scrollToTop();
            },
        },
    };

    render(...args) {
        pageInfo(PAGES.BUSINESSES);
        scrollToTop();
		searchPlaceholder("Search for a business...")
        this.page = 1;
        this.lastPage = false;
        this.isLoading = false;
        this.searchSeq++;
        this.filters.value = { nearby: false, all: true, deals: false };
        this.latitude = null;
        this.longitude = null;

        setCurrentLocationCoordinates(
            false,
            (latitude, longitude) => {
                this.latitude = latitude;
                this.longitude = longitude;

                if (this.filters.value.nearby != true) return;

                let prevPage = this.page;

                this.page = 1;
                this.lastPage = false;
                this.isLoading = false;
                this.searchSeq++;
                dom.empty(dom.id(`b-profile`));

                // Only auto-refetch if nothing (or just the first page) had
                // loaded yet - once the visitor has scrolled further than
                // that, silently resetting the list out from under them
                // would be jarring.
                if (prevPage == 1 || prevPage == 2) {
                    this.getBusinesses("b-profile");
                }
            },
            true,
        );

        return h.div(
            {
                class: "mb-5 pb-5 pt-3 py-md-5 container",
            },
            h.div(
                {
                    class: "sticky-top d-flex justify-content-center mb-4",
                    style: "top: 75px; z-index: 1020;",
                },
                v(this.filters, (filters) => {
                    let f = filters.value;

                    return h.div(
                        {
                            class: "d-flex p-1 bg-white border rounded-pill shadow-sm",
                            style: "width: 100%; max-width: 350px;",
                        },
                        h.button(
                            {
                                class: `btn rounded-pill flex-fill d-flex align-items-center gap-2 justify-content-center fw-bold border-0 ${f.nearby ? "btn-dark shadow-sm text-white" : "text-secondary text-decoration-none"}`,
                                style: "transition: all 0.2s ease;",
                                onclick: this.method("selectFilter", [
                                    "nearby",
                                ]),
                            },
                            h.SolidIcon("location-pin"),
                            h.span("Near Me"),
                        ),
                        h.button(
                            {
                                class: `btn rounded-pill flex-fill d-flex align-items-center gap-2 justify-content-center fw-bold border-0 ${f.all ? "btn-dark shadow-sm text-white" : "text-secondary text-decoration-none"}`,
                                style: "transition: all 0.2s ease;",
                                onclick: this.method("selectFilter", ["all"]),
                            },
                            h.SolidIcon("globe"),
                            h.span("All"),
                        ),
                    );
                }),
            ),
            h.InfiniteScroll({
                child: h.div(
                    {
                        id: `b-profile`,
                        class: "pt-3 row g-3 mb-3",
                    },
                    ...args,
                ),
                callback: (entries, observer, marker) => {
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) return;
                        this.populateBusinesses("b-profile", marker);
                    });
                },
            }),
        );
    }

    async getBusinesses(holderId, spinnerHolder = null) {
        if (this.lastPage || this.isLoading) {
            return;
        }

        this.isLoading = true;

        const requestSeq = ++this.searchSeq;

        // Request whatever page is currently "next" - it isn't bumped
        // until a response confirms it (below), so a duplicate/overlapping
        // call (see the isLoading guard above, and the class-field comment
        // on why one is possible in the first place) just redundantly
        // re-requests the same page instead of racing ahead and skipping
        // one - the card-level de-dup check further down makes a redundant
        // response harmless to render too.
        let page = this.page;
        let query = dc.searchQuery.value ?? "";
        let filters = tool.deepCopy(this.filters.value);
        let spinner = null;

        if (spinnerHolder) {
            spinner = h.Spinner({ parent: spinnerHolder, resetParent: true });
        }

        let qs = { page, interval: 15 };

        filters.latitude = this.latitude;
        filters.longitude = this.longitude;

        if (!empty(query)) {
            qs.query = query.trim();
        }

        qs.filter = JSON.stringify(filters);

        try {
            let result = await api.get(`${routes.businesses}/search`, qs);

            // Stale response - a newer request (a fresh search, filter
            // change, etc.) has since been dispatched. Don't let it
            // overwrite the newer, correct results.
            if (requestSeq !== this.searchSeq) {
                return;
            }

            dom.id(`bp-no-business-found`)?.remove();

            if (result) {
                let businesses = result.data.companies.data;
                let currentPage = result.data.companies.current_page;
                let lastPageNum = result.data.companies.last_page;
                this.total = result.data.companies.total ?? 0;

                let holder = dom.id(holderId);

                if (currentPage == 1) {
                    dom.empty(holder);
                }

                for (let business of businesses) {
                    if (!dom.get(`[biz-card-id="b-${business.id}"]`, holder)) {
                        h.BusinessesView(business, {
                            parent: holder,
                            $class: "bg-white col-12 col-md-6 col-lg-4",
                            $biz_card_id: `b-${business.id}`,
                        });
                    }
                }

                if (empty(businesses) && currentPage == 1) {
                    h.div(
                        {
                            parent: holder,
                            resetParent: true,
                            class: "col-12 py-5 d-flex justify-content-center",
                            id: "bp-no-business-found",
                        },
                        h.div(
                            {
                                class: "card border-0 shadow-sm rounded-4 text-center p-5 w-100",
                                style: "max-width: 400px; background: rgba(255, 255, 255, 0.9); backdrop-filter: blur(10px);",
                            },
                            h.div(
                                {
                                    class: "rounded-circle mx-auto d-flex align-items-center justify-content-center mb-3 shadow-sm",
                                    style: "width: 72px; height: 72px; background: linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%);",
                                },
                                h.i({
                                    class: "bi bi-shop text-primary",
                                    style: "font-size: 2rem; color: #a855f7 !important;",
                                }),
                            ),
                            h.h5(
                                { class: "fw-bold text-dark mb-2" },
                                "You're all caught up",
                            ),
                            h.p(
                                { class: "text-secondary mb-0 small" },
                                "There are no businesses to show right now. Try adjusting your filters or search terms.",
                            ),
                        ),
                    );
                }

                // Only now, once the server has confirmed what page this
                // actually was, do we decide what "next" means - never
                // optimistically ahead of a response.
                if (empty(businesses) || currentPage >= lastPageNum) {
                    this.lastPage = true;
                } else {
                    this.page = currentPage + 1;
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            spinner?.remove();
            this.isLoading = false;
        }
    }

    async populateBusinesses(holderId, marker) {
        this.getBusinesses(holderId, marker);
    }

    selectFilter(key) {
        let filters = tool.deepCopy(this.filters.value);

        if (filters[key] == true) {
            filters[key] = false;
        } else {
            filters[key] = true;
        }

        let hasKey = false;

        for (let k in filters) {
            if (key != k) {
                filters[k] = false;
            }

            if (filters[k] == true) {
                hasKey = true;
            }
        }

        if (!hasKey) {
            filters["all"] = true;
        }

        this.filters.value = filters;

        let prevPage = this.page;

        this.page = 1;
        this.lastPage = false;
        this.isLoading = false;
        this.searchSeq++;
        dom.empty(dom.id(`b-profile`));

        // Same "only auto-refetch if nothing (or just page 1) had loaded
        // yet" rule as the geolocation callback above.
        if (prevPage == 1 || prevPage == 2) this.getBusinesses("b-profile");
    }
}

class BusinessesView extends Component {
    render(businessData, ...args) {
        let bState = state(businessData);

        return h.div(
            v(bState, (bDataState) => {
                let business = bDataState.value;
                let initial = business?.name
                    ? business.name.charAt(0).toUpperCase()
                    : "?";

                return h.div(
                    {
                        class: "biz-view-card h-100 rounded-4 border overflow-hidden position-relative shadow-sm text-decoration-none",
                        role: "button",
                        onclick: h.func("route.to", "business.view-profile", {
                            businessId: business?.id,
                        }),
                    },
                    business?.cover_path
                        ? h.div({
                              class: "position-absolute top-0 start-0 w-100 h-100",
                              style: `background: url('${business.cover_path}') no-repeat center center; background-size: cover; z-index: 0;`,
                          })
                        : h.div(
                              {
                                  class: "position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center overflow-hidden",
                                  style: `background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); z-index: 0;`,
                              },
                              h.span(
                                  {
                                      class: "text-white fw-bold opacity-25 user-select-none",
                                      style: "font-size: 10rem; transform: rotate(-15deg); line-height: 1;",
                                  },
                                  initial,
                              ),
                          ),
                    h.div(
                        {
                            class: "position-absolute",
                            style: "top: 15px; right: 15px; z-index: 10;",
                        },
                        h.button(
                            {
                                class: "btn btn-light rounded-circle shadow-sm p-0 d-flex align-items-center justify-content-center border-0",
                                style: "width: 35px; height: 35px; background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(4px);",
                                listeners: {
                                    click: (e) =>
                                        this.toggleBookmark(bState, e),
                                },
                            },
                            h.i({
                                class: business?.currently_bookmarked
                                    ? "bi bi-bookmark-fill text-primary"
                                    : "bi bi-bookmark text-secondary",
                                style: "font-size: 1.1rem;",
                            }),
                        ),
                    ),

                    // Rating badge - mirrors the bookmark button on the
                    // opposite corner, so it doesn't disturb the name/
                    // handle footer layout below. Outline (not solid) star,
                    // in the brand purple rather than a shouty gold, plus
                    // the number of people who left a rating.
                    either(
                        formatRating(business?.avg_rating),
                        () =>
                            h.div(
                                {
                                    class: "position-absolute d-flex align-items-center gap-1 rounded-pill shadow-sm",
                                    style: "top: 15px; left: 15px; z-index: 10; background: rgba(255, 255, 255, 0.9); backdrop-filter: blur(4px); padding: 4px 10px;",
                                },
                                h.i({
                                    class: "bi bi-star text-primary",
                                    style: "font-size: 0.7rem;",
                                }),
                                h.span(
                                    {
                                        class: "fw-bold text-dark",
                                        style: "font-size: 0.75rem;",
                                    },
                                    formatRating(business?.avg_rating),
                                ),
                                business?.rating_count
                                    ? h.span(
                                          {
                                              class: "text-muted",
                                              style: "font-size: 0.7rem;",
                                          },
                                          `(${business.rating_count})`,
                                      )
                                    : null,
                            ),
                    ),
                    h.div(
                        {
                            class: "d-flex flex-column h-100 position-relative w-100",
                            style: "z-index: 1; min-height: 200px;",
                        },
                        h.div({ class: "flex-fill w-100" }),
                        h.div(
                            {
                                class: "p-2 d-flex gap-3 align-items-center w-100",
                                style: "background: rgba(0, 0, 0, 0.65);",
                            },
                            h.div(
                                {
                                    class: "flex-shrink-0 position-relative",
                                    style: "width: 40px; height: 40px;",
                                },
                                business?.logo_path
                                    ? h.img({
                                          class: "rounded shadow-sm position-absolute",
                                          style: "width: 40px; height: 40px; object-fit: cover; top: 0; left: 0;",
                                          src: business.logo_path,
                                      })
                                    : h.div(
                                          {
                                              class: "rounded shadow-sm position-absolute d-flex align-items-center justify-content-center fw-bold text-white fs-5",
                                              style: "width: 40px; height: 40px; top: 0; left: 0; background: linear-gradient(135deg, #0d6efd 0%, #0098f0 100%);",
                                          },
                                          initial,
                                      ),
                            ),
                            h.div(
                                {
                                    class: "flex-fill d-flex flex-column justify-content-center overflow-hidden",
                                },
                                h.div(
                                    {
                                        class: "text-truncate fw-bold text-white mb-0",
                                    },
                                    business?.name,
                                ),
                                h.div(
                                    { class: "text-white-50 small" },
                                    "@",
                                    business?.handle,
                                ),
                            ),
                        ),
                    ),
                );
            }),
            ...args,
        );
    }

    async toggleBookmark(bState, event) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        const company = bState.value;
        const bookmarked = company.currently_bookmarked;
        const url = `/company-bookmarks/${company.id}`;

        try {
            let res;
            if (bookmarked) {
                res = await api.delete(url);
                if (res) {
                    inform(res.message || "Bookmark removed", STRINGS.SUCCESS);
                    bState.value = {
                        ...company,
                        currently_bookmarked: false,
                        save_count: Math.max(0, (company.save_count || 0) - 1),
                    };
                }
            } else {
                res = await api.post(url);
                if (res) {
                    inform(
                        res.message || "Company bookmarked",
                        STRINGS.SUCCESS,
                    );
                    bState.value = {
                        ...company,
                        currently_bookmarked: true,
                        save_count: (company.save_count || 0) + 1,
                    };
                }
            }
        } catch (e) {
            inform(
                e.response?.data?.message || "Failed to update bookmark",
                STRINGS.ERROR,
            );
        }
    }
}

ojs(BusinessesPage, BusinessesView);
